import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  VendorWithRelations,
  VendorCategory,
  VendorUsage,
  CreateVendorInput,
  UpdateVendorInput,
  AddVendorCategoryInput,
  AddVendorContactInput,
  UpdateVendorContactInput,
  VendorContactPortalAccessInput,
  VendorPaymentMethodInput,
} from '@peles/shared';
import * as vendorsApi from '../api/vendors';

export function useVendors(
  filters?: {
    isActive?: boolean;
    city?: string;
    vendorCategoryId?: string;
  },
  options?: { enabled?: boolean },
) {
  return useQuery<VendorWithRelations[]>({
    queryKey: ['vendors', filters],
    queryFn: () => vendorsApi.listVendors(filters),
    enabled: options?.enabled ?? true,
  });
}

export function useVendor(id: string) {
  return useQuery<VendorWithRelations>({
    queryKey: ['vendors', id],
    queryFn: () => vendorsApi.getVendor(id),
    enabled: !!id,
  });
}

export function useCreateVendor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateVendorInput) => vendorsApi.createVendor(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
}

export function useUpdateVendor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateVendorInput }) =>
      vendorsApi.updateVendor(id, updates),
    onSuccess: (updatedVendor) => {
      queryClient.setQueryData(['vendors', updatedVendor.id], updatedVendor);
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
}

export function useDeactivateVendor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => vendorsApi.deactivateVendor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
}

export function useVendorUsage(id: string, enabled = true) {
  return useQuery<VendorUsage>({
    queryKey: ['vendors', id, 'usage'],
    queryFn: () => vendorsApi.getVendorUsage(id),
    enabled: enabled && !!id,
  });
}

export function useDeleteVendor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reassignToVendorId }: { id: string; reassignToVendorId: string | null }) =>
      vendorsApi.deleteVendor(id, reassignToVendorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      // Reassigned records live under other query roots
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['entity-links'] });
    },
  });
}

export function useAddVendorCategory(vendorId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddVendorCategoryInput) => vendorsApi.addVendorCategory(vendorId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors', vendorId] });
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
}

export function useRemoveVendorCategory(vendorId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (categoryId: string) => vendorsApi.removeVendorCategory(vendorId, categoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors', vendorId] });
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
}

export function useAddVendorContact(vendorId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddVendorContactInput) => vendorsApi.addVendorContact(vendorId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors', vendorId] });
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
}

export function useUpdateVendorContact(vendorId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, ...data }: UpdateVendorContactInput & { userId: string }) =>
      vendorsApi.updateVendorContact(vendorId, userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors', vendorId] });
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
}

export function useSetVendorContactPortalAccess(vendorId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, ...input }: VendorContactPortalAccessInput & { userId: string }) =>
      vendorsApi.setVendorContactPortalAccess(vendorId, userId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors', vendorId] });
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      // A person's account changed: the users list shows their role and state.
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useRemoveVendorContact(vendorId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => vendorsApi.removeVendorContact(vendorId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors', vendorId] });
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
}

export function useVendorCategories() {
  return useQuery<VendorCategory[]>({
    queryKey: ['vendor-categories'],
    queryFn: () => vendorsApi.listVendorCategories(),
    staleTime: Infinity,
  });
}

/** Payment methods are read with the vendor, so every change refetches it. */
function useVendorPaymentMutation<TVars>(
  vendorId: string,
  mutationFn: (vars: TVars) => Promise<unknown>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors', vendorId] });
    },
  });
}

export function useAddVendorPaymentMethod(vendorId: string) {
  return useVendorPaymentMutation(vendorId, (input: VendorPaymentMethodInput) =>
    vendorsApi.addVendorPaymentMethod(vendorId, input),
  );
}

export function useUpdateVendorPaymentMethod(vendorId: string) {
  return useVendorPaymentMutation(
    vendorId,
    ({ methodId, input }: { methodId: string; input: VendorPaymentMethodInput }) =>
      vendorsApi.updateVendorPaymentMethod(vendorId, methodId, input),
  );
}

export function useRemoveVendorPaymentMethod(vendorId: string) {
  return useVendorPaymentMutation(vendorId, (methodId: string) =>
    vendorsApi.removeVendorPaymentMethod(vendorId, methodId),
  );
}
